import React from "react";
import { useLocation } from "react-router-dom";
import { SCHEMA_MAPPING, SERVICE_DATA, LOCATION_DATA } from "./schemaMapping";
import {
  HomePageSchema,
  AboutUsSchema,
  CloudServicesSchema,
  CloudPlatformSchema,
  ColocationSchema,
  NetworkServiceSchema,
  SystemsIntegrationSchema,
  CybersecuritySchema,
  SovereignAiCloudSchema,
  ContactSchema,
  CareersSchema,
  EventsSchema,
  PartnersSchema,
  CustomersSchema,
  AppleServicesSchema,
  DellServicesSchema,
  InternetServicesSchema,
  CloudCalculatorSchema,
  PrivacyPolicySchema,
  TermsConditionsSchema,
  CloudPlansSchema,
  JcnlSchema,
} from "./pageSchemas";

import {
  ManagedServices,
  EnterpriceConnectivity,
  UnifiedCommunication,
  HighSpeedBroadband,
  HighSpeedBroadband_Plans,
  CarrierBasedCloud,
  Colocation,
  SecureAndScalableDigital,
  DataLifeCycleSolutions,
  SmartCityServices,
} from "./servicesSchemas";

// Schema component mapping
const SCHEMA_COMPONENTS = {
  HomePageSchema,
  AboutUsSchema,
  CloudServicesSchema,
  CloudPlatformSchema,
  ColocationSchema,
  NetworkServiceSchema,
  SystemsIntegrationSchema,
  CybersecuritySchema,
  SovereignAiCloudSchema,
  ContactSchema,
  CareersSchema,
  EventsSchema,
  PartnersSchema,
  CustomersSchema,
  AppleServicesSchema,
  DellServicesSchema,
  InternetServicesSchema,
  CloudCalculatorSchema,
  PrivacyPolicySchema,
  TermsConditionsSchema,
  ManagedServices,
  EnterpriceConnectivity,
  UnifiedCommunication,
  HighSpeedBroadband,
  HighSpeedBroadband_Plans,
  CarrierBasedCloud,
  Colocation,
  SecureAndScalableDigital,
  DataLifeCycleSolutions,
  SmartCityServices,
  CloudPlansSchema,
  JcnlSchema,
};

// Universal Schema Hook
export const usePageSchema = () => {
  const location = useLocation();
  const pathname = location.pathname;

  const getSchemaComponent = () => {
    const schemaType = SCHEMA_MAPPING[pathname];
    if (!schemaType) return null;

    const SchemaComponent = SCHEMA_COMPONENTS[schemaType];
    if (!SchemaComponent) return null;

    // Handle dynamic schemas with props
    if (schemaType === "ColocationSchema") {
      const locationKey = pathname.split("/").pop();
      const locationData = LOCATION_DATA[locationKey];
      return (
        <SchemaComponent
          location={locationData?.name || locationKey}
          image={locationData?.image}
          address={locationData?.address}
        />
      );
    }

    if (schemaType === "NetworkServiceSchema") {
      const serviceKey = pathname.split("/").pop();
      const serviceData = SERVICE_DATA[serviceKey];
      return (
        <SchemaComponent
          serviceName={serviceData?.name || "Network Services"}
          url={`https://ishantechnologies.com${pathname}`}
          description={
            serviceData?.description || "Professional network services"
          }
        />
      );
    }

    if (schemaType === "SystemsIntegrationSchema") {
      const serviceKey = pathname.split("/").pop();
      const serviceData = SERVICE_DATA[serviceKey];
      return (
        <SchemaComponent
          serviceName={serviceData?.name || "Systems Integration"}
          url={`https://ishantechnologies.com${pathname}`}
          description={
            serviceData?.description ||
            "Professional systems integration services"
          }
        />
      );
    }

    if (schemaType === "AppleServicesSchema") {
      const serviceKey = pathname.split("/").pop();
      const serviceData = SERVICE_DATA[`apple-${serviceKey}`];
      return (
        <SchemaComponent
          serviceName={serviceData?.name || "Apple Services"}
          url={`https://ishantechnologies.com${pathname}`}
          description={
            serviceData?.description || "Professional Apple enterprise services"
          }
        />
      );
    }

    if (schemaType === "ContactSchema") {
      const contactType = pathname.includes("sales")
        ? "Sales"
        : pathname.includes("customer-service")
          ? "Customer Service"
          : pathname.includes("media")
            ? "Media"
            : pathname.includes("partner")
              ? "Partnership"
              : "Contact";
      return (
        <SchemaComponent
          contactType={contactType}
          url={`https://ishantechnologies.com${pathname}`}
        />
      );
    }

    return <SchemaComponent />;
  };

  return getSchemaComponent();
};

// Page Schema Component - Use this in any page
export const PageSchema = () => {
  const schemaComponent = usePageSchema();
  return schemaComponent || null;
};
